import { useEffect } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";

import { useUserSession } from "@/hooks/useUserSession";
import { PermissionsProvider } from "@/lib/permissions";
import { useTenantStore } from "@/stores/tenant-store";

/**
 * Protects routes that require authentication.
 *
 * Uses our custom useUserSession hook which extends better-auth's session
 * with tenant memberships and permissions.
 *
 * Wraps children in PermissionsProvider for permission checking.
 */
export function ProtectedRoute() {
  const { session, isLoading } = useUserSession();
  const location = useLocation();
  const { activeTenantId, setActiveTenant, setTenants } = useTenantStore();

  // Sync tenants from session to store
  useEffect(() => {
    if (session?.tenants) {
      setTenants(
        session.tenants.map((t) => ({
          id: t.tenantId,
          name: t.tenantName,
        }))
      );

      // Auto-select tenant if none selected
      if (!activeTenantId && session.tenants.length > 0) {
        const defaultTenant =
          session.tenants.find((t) => t.isDefault) ?? session.tenants[0];
        if (defaultTenant) {
          setActiveTenant(defaultTenant.tenantId, {
            id: defaultTenant.tenantId,
            name: defaultTenant.tenantName,
          });
        }
      }
    }
  }, [session?.tenants, activeTenantId, setActiveTenant, setTenants]);

  // Show loading spinner while checking session
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!session) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Redirect to onboarding if user has no tenants
  if (session.tenants.length === 0) {
    return <Navigate to="/onboarding" replace />;
  }

  // Render child routes wrapped in PermissionsProvider
  return (
    <PermissionsProvider
      tenants={session.tenants}
      currentTenantId={activeTenantId}
      isLoading={isLoading}
    >
      <Outlet />
    </PermissionsProvider>
  );
}
